/**
 * Top-level layout: sticky header with nav + outlet for the active page.
 */
import { NavLink, Outlet } from "react-router-dom";
import {
  Disc3,
  ListMusic,
  PlayCircle,
  BarChart3,
  Settings,
  Wrench,
  LogOut,
} from "lucide-react";
import { useAuthMe, useLogout } from "../hooks/useAuth";
import { usePlaybackSync } from "../hooks/usePlaybackSync";
import { APP_COPYRIGHT, APP_VERSION_LABEL } from "@memes/shared";
import { ThemeToggle } from "./ThemeToggle";
import { AudioEngine } from "./AudioEngine";
import { Player } from "./Player";

const links = [
  { to: "/", label: "Library", icon: Disc3, end: true },
  { to: "/queue", label: "Queue", icon: ListMusic },
  { to: "/now-playing", label: "Now Playing", icon: PlayCircle },
  { to: "/stats", label: "Stats", icon: BarChart3 },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/dev", label: "Dev", icon: Wrench },
];

export const Layout: React.FC = () => {
  const auth = useAuthMe();
  const logout = useLogout();
  const user = auth.data?.user;
  usePlaybackSync();

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-20 border-b bg-white/80 backdrop-blur dark:bg-gray-950/80 dark:border-gray-800">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-3">
            <img
              src="/icon.png"
              alt=""
              className="h-9 w-9 rounded-xl object-cover shadow"
            />
            <div>
              <div className="text-sm font-semibold">Memes &amp; Shit</div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">
                {APP_VERSION_LABEL}
              </div>
            </div>
          </div>
          <nav className="hidden items-center gap-1 md:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                  }`
                }
              >
                <l.icon className="h-4 w-4" />
                <span>{l.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <div className="hidden items-center gap-2 text-xs text-gray-500 md:flex">
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {user.globalName ?? user.username}
                </span>
                <button
                  type="button"
                  className="btn p-1.5"
                  onClick={() => void logout.mutateAsync()}
                  aria-label="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : null}
            <ThemeToggle />
          </div>
        </div>
      </header>

      <AudioEngine />

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-44 pt-6">
        <Outlet />
      </main>

      {/* Persistent player docked at bottom */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t bg-white/95 backdrop-blur dark:bg-gray-950/95 dark:border-gray-800">
        <div className="mx-auto max-w-6xl px-4 py-3">
          <Player />
          <p className="mt-1 text-center text-[10px] text-gray-400 dark:text-gray-500">
            {APP_COPYRIGHT}
          </p>
        </div>
      </div>
    </div>
  );
};
